import { useVirtualizer } from "@tanstack/react-virtual";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { ActivityDriverSection } from "./ActivityDriverSection";
import {
  ACTIVITY_GRID_MIN_WIDTH_CLASS,
  ACTIVITY_ROW_INSET_CLASS,
  type ActivityDensity,
  activityGridStyle,
  activityGroupStartClass,
  activityMutedClass,
  activityRowClass,
} from "./driver-activity-layout";
import {
  type ActivityDriver,
  type ActivityDurationMode,
  type ActivityIdLabel,
} from "./driver-activity-view-model";
import { DRIVER_ACTIVITY_ROW_SETTING_KEYS, type DriverActivityRowSettings, DriverActivityRowSettingsProvider } from "./DriverActivityRowContext";

/** The grid's settings (see `DriverActivityRowSettings`) plus what only the list itself needs. */
export type DriverActivityListProps = DriverActivityRowSettings & {
  drivers: ActivityDriver[];
  /** Keys of the drivers whose activity is folded away. */
  collapsed?: ReadonlySet<string>;
  /**
   * The element that scrolls the list. With it, a day of `virtualizeFrom` rows or more renders only the
   * drivers on screen.
   */
  getScrollElement?: () => HTMLElement | null;
  /** Number of rows from which the list virtualizes; 150 by default. */
  virtualizeFrom?: number;
};

const NONE: ReadonlySet<string> = new Set();

const durationLabels: Record<ActivityDurationMode, string> = {
  turnaround: "Turn.",
  onSite: "On site",
  travel: "Travel",
  billable: "Billable",
};

export const ActivityColumnHeader = ({ showRevenue, durationMode = "turnaround", idLabel = "resourceId" }: { showRevenue: boolean; durationMode?: ActivityDurationMode; idLabel?: ActivityIdLabel }) => {
  const label = "px-2 text-[11px] font-semibold uppercase tracking-wide";
  const column = [label, activityMutedClass].join(" ");
  const group = [label, activityGroupStartClass, "flex h-full items-center"].join(" ");

  return (
    <div role="rowgroup" className="sticky top-0 z-20 border-b border-[var(--ots-outline)] bg-[var(--ots-surface)] shadow-sm">
      <div aria-hidden="true" className={[activityRowClass, ACTIVITY_ROW_INSET_CLASS, "h-6 items-center"].join(" ")}>
        <span className="col-span-4"/>
        <span className={[group, "col-span-3 border-t-2 border-t-[var(--ots-success)] text-[var(--ots-success)]"].join(" ")}>Pickup</span>
        <span className={[group, "col-span-3 border-t-2 border-t-[var(--ots-error)] text-[var(--ots-error)]"].join(" ")}>Delivery</span>
        <span className={[group, showRevenue ? "col-span-4" : "col-span-3", "border-t-2 border-t-[var(--ots-primary)] text-[var(--ots-primary)]"].join(" ")}>Details</span>
      </div>
      <div role="row" className={[activityRowClass, ACTIVITY_ROW_INSET_CLASS, "h-7 items-center"].join(" ")}>
        <span role="columnheader" aria-label="Select"/>
        <span role="columnheader" aria-label="Warnings"/>
        <span role="columnheader" aria-label="Audit"/>
        <span role="columnheader" className={[column, "!px-1"].join(" ")}>{idLabel === "ticketNumber" ? "Ticket #" : "ID"}</span>
        <span role="columnheader" className={[column, activityGroupStartClass].join(" ")}>Time · Geofence</span>
        <span role="columnheader" className={column}>Site / Tank / Owner</span>
        <span role="columnheader" className={[column, "text-right"].join(" ")}>Qty</span>
        <span role="columnheader" className={[column, activityGroupStartClass].join(" ")}>Time · Geofence</span>
        <span role="columnheader" className={column}>Site / Tank / Owner</span>
        <span role="columnheader" className={[column, "text-right"].join(" ")}>Qty</span>
        <span role="columnheader" className={[column, activityGroupStartClass].join(" ")}>Freight · Work</span>
        <span role="columnheader" className={[column, "text-right"].join(" ")}>{durationLabels[durationMode]}</span>
        {showRevenue && <span role="columnheader" className={[column, "text-right"].join(" ")}>Rev. / Cost</span>}
        <span role="columnheader" className={column}>Status</span>
      </div>
    </div>
  );
};

/**
 * The room above and below the drivers on screen, as padding on their container. Item offsets include the scroll
 * margin and the total size does not, so the margin is added back; without that, the space under the last driver
 * came out negative, which browsers reject, leaving the last valid (and far too tall) value as empty space.
 */
export const virtualSpacers = (items: readonly { start: number; end: number }[], totalSize: number, scrollMargin: number) => ({
  paddingTop: items.length ? Math.max(0, items[0].start - scrollMargin) : 0,
  paddingBottom: Math.max(0, items.length ? totalSize + scrollMargin - items[items.length - 1].end : totalSize),
});

/**
 * Rough height of a driver's card before it has been measured: header, shift rows, and entries.
 * Only has to be close; the virtualizer corrects it once the card renders.
 */
const estimateDriverHeight = (driver: ActivityDriver, collapsed: boolean, density: ActivityDensity) => {
  const row = density === "compact" ? 36 : 46;
  if (collapsed) return 72;
  return 88 + driver.shifts.reduce((height, shift) => height + 76 + shift.entries.length * row, 0);
};

export type DriverActivityListHandle = {
  /** Brings a driver's card into view, rendering it first if it was virtualized away. */
  scrollToDriver: (driverKey: string) => void;
};

/**
 * Drivers → start shift → activity → end shift, in one aligned grid with sticky column and driver headers.
 * Given its scroll container, a long day renders only the drivers on screen.
 */
export const DriverActivityList = forwardRef<DriverActivityListHandle, DriverActivityListProps>(function DriverActivityList({
  drivers,
  showRevenue = false,
  durationMode = "turnaround",
  density = "default",
  collapsed = NONE,
  getScrollElement,
  virtualizeFrom = 150,
  ...rest
}, ref) {
  // One value for every card and row; rebuilt only when a setting changes, so cards stay memoized.
  const given: DriverActivityRowSettings = { ...rest, showRevenue, durationMode, density };
  const settings = useMemo<DriverActivityRowSettings>(
    () => given,
    // The settings one by one: a new value only when one of them changes.
    DRIVER_ACTIVITY_ROW_SETTING_KEYS.map((key) => given[key]),
  );
  const rowCount = useMemo(() => drivers.reduce((count, driver) => count + driver.shifts.reduce((rows, shift) => rows + shift.entries.length, 0), 0), [drivers]);
  const virtual = !!getScrollElement && rowCount >= virtualizeFrom;
  const virtualizer = useVirtualizer({
    count: virtual ? drivers.length : 0,
    getScrollElement: getScrollElement ?? (() => null),
    estimateSize: (index) => estimateDriverHeight(drivers[index], collapsed.has(drivers[index].key), density),
    getItemKey: (index) => drivers[index].key,
    overscan: 3,
    // The sticky column header sits above the first card.
    scrollMargin: 52,
  });

  useImperativeHandle(ref, () => ({
    scrollToDriver: (driverKey) => {
      const index = drivers.findIndex((driver) => driver.key === driverKey);
      if (virtual && index >= 0) virtualizer.scrollToIndex(index, { align: "start" });
    },
  }), [drivers, virtual, virtualizer]);

  const items = virtualizer.getVirtualItems();
  const renderDriver = (driver: ActivityDriver, index?: number) => (
    <ActivityDriverSection
      key={driver.key}
      ref={index != null ? virtualizer.measureElement : undefined}
      index={index}
      driver={driver}
      isCollapsed={collapsed.has(driver.key)}/>
  );

  return (
    <DriverActivityRowSettingsProvider value={settings}>
    <div
      role="grid"
      aria-label="Driver activity"
      aria-multiselectable="true"
      aria-rowcount={virtual ? rowCount : undefined}
      data-virtual={virtual || undefined}
      // Room under the last card only while the floating selection bar covers it.
      className={[ACTIVITY_GRID_MIN_WIDTH_CLASS, "@container", settings.selected?.size ? "pb-20" : "pb-3"].join(" ")}
      style={activityGridStyle(showRevenue, density)}>
      <ActivityColumnHeader showRevenue={showRevenue} durationMode={durationMode} idLabel={settings.idLabel}/>
      {virtual
        // Spacers above and below keep the cards in normal flow, so their sticky headers still work.
        ? <div role="presentation" style={virtualSpacers(items, virtualizer.getTotalSize(), virtualizer.options.scrollMargin)}>
          {items.map((item) => renderDriver(drivers[item.index], item.index))}
        </div>
        : drivers.map((driver) => renderDriver(driver))}
    </div>
    </DriverActivityRowSettingsProvider>
  );
});
